import React, { useState, useEffect } from 'react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import Typography from '@mui/material/Typography';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  IconButton,
  Collapse,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import './new-customized-table.scss';

export interface TableColumn<T = Record<string, unknown>> {
  id: keyof T;
  label: string;
  align?: 'left' | 'right' | 'center';
  sortable?: boolean;
  render?: (value: T[keyof T], row: T, rowIndex?: number) => React.ReactNode;
  width?: string | number;
  minWidth?: string | number;
  flex?: number | string;
  headerTestId?: string;
}

export interface CustomizedTableProps<T = Record<string, unknown>> {
  title?: string;
  className?: string;
  columns: TableColumn<T>[];
  message?: string | null;
  data: T[];
  enableSorting?: boolean;
  enablePagination?: boolean;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
  maxHeight?: string | number;
  loading?: boolean;
  rowTestIdPrefix?: string;
  // Server-side sorting props
  onSortChange?: (columnId: keyof T, sortOrder: 'asc' | 'desc') => void;
  onSortReset?: () => void;
  currentSortColumn?: keyof T;
  currentSortOrder?: 'asc' | 'desc';
  // Server-side pagination props
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  currentPage?: number;
  totalItems?: number;
  // Expandable rows props
  expandableRows?: boolean;
  expandedContent?: (rowId: string | number, row: T) => React.ReactNode;
  showExpandIcon?: (row: T) => boolean;
  getRowId?: (row: T) => string | number;
}

type Order = 'asc' | 'desc';

// Helper functions to create common column types
export const createTextColumn = <T = Record<string, unknown>,>(
  id: keyof T,
  label: string,
  options?: Partial<TableColumn<T>>
): TableColumn<T> => ({
  id,
  label,
  sortable: true,
  align: 'left',
  ...options,
});

export const createLinkColumn = <T = Record<string, unknown>,>(
  id: keyof T,
  label: string,
  onLinkClick: (value: T[keyof T], row: T) => void,
  options?: Partial<TableColumn<T>>
): TableColumn<T> => ({
  id,
  label,
  sortable: false,
  align: 'left',
  render: (value, row) => (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      href="#"
      style={{
        color: '#1976d2',
        textDecoration: 'underline',
        cursor: 'pointer',
      }}
      onClick={(e) => {
        e.preventDefault();
        onLinkClick(value, row);
      }}
    >
      {String(value)}
    </a>
  ),
  ...options,
});

export const createDropdownColumn = <T = Record<string, unknown>,>(
  id: keyof T,
  label: string,
  options: { value: string; label: string }[],
  onChange: (newValue: string, row: T) => void,
  columnOptions?: Partial<TableColumn<T>>
): TableColumn<T> => ({
  id,
  label,
  sortable: true,
  align: 'left',
  render: (value, row) => (
    <FormControl
      size="small"
      className="new-customized-table-dropdown-form-control"
    >
      <Select
        value={value as string}
        onChange={(e) => onChange(e.target.value, row)}
        className="new-customized-table-dropdown-select"
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ),
  ...columnOptions,
});

export const createActionColumn = <T = Record<string, unknown>,>(
  id: keyof T,
  label: string,
  actions: Array<{
    label: string;
    onClick: (row: T) => void;
    color?: string;
  }>,
  columnOptions?: Partial<TableColumn<T>>
): TableColumn<T> => ({
  id,
  label,
  sortable: false,
  align: 'center',
  render: (value, row) => (
    <Box className="new-customized-table-action-buttons-container">
      {actions.map((action, index) => (
        <button
          key={index}
          onClick={() => action.onClick(row)}
          className="new-customized-table-action-button"
          style={{
            backgroundColor: action.color || '#f5f5f5',
          }}
        >
          {action.label}
        </button>
      ))}
    </Box>
  ),
  ...columnOptions,
});

export interface StatusConfig {
  activeColor?: string;
  inactiveColor?: string;
  activeLabel?: string;
  inactiveLabel?: string;
  borderWidth?: string;
}

// when we want to show active or inactive with border
export const createStatusColumn = <T = Record<string, unknown>,>(
  id: keyof T,
  label: string,
  config?: StatusConfig,
  columnOptions?: Partial<TableColumn<T>>
): TableColumn<T> => {
  const defaultConfig: Required<StatusConfig> = {
    activeColor: '#4caf50',
    inactiveColor: '#f44336',
    activeLabel: 'Active',
    inactiveLabel: 'Inactive',
    borderWidth: '2px',
  };

  const finalConfig = { ...defaultConfig, ...config };

  return {
    id,
    label,
    sortable: true,
    align: 'left',
    render: (value, row) => {
      const isActive = value === 'active' || value === true;
      const displayLabel = isActive
        ? finalConfig.activeLabel
        : finalConfig.inactiveLabel;
      const borderColor = isActive
        ? finalConfig.activeColor
        : finalConfig.inactiveColor;

      return (
        <Box
          className="new-customized-table-status-badge"
          sx={{
            border: `${finalConfig.borderWidth} solid ${borderColor}`,
            color: borderColor,
          }}
        >
          {displayLabel}
        </Box>
      );
    },
    ...columnOptions,
  };
};

export interface ColorConfig {
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  fontWeight?: string | number;
  fontSize?: string;
  padding?: string;
  textAlign?: 'left' | 'center' | 'right';
}

export const createColoredColumn = <T = Record<string, unknown>,>(
  id: keyof T,
  label: string,
  colorConfig?: ColorConfig,
  columnOptions?: Partial<TableColumn<T>>
): TableColumn<T> => {
  const defaultConfig: Required<ColorConfig> = {
    color: '#000000',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    borderWidth: '0px',
    borderRadius: '4px',
    fontWeight: '600',
    fontSize: '14px',
    padding: '4px 8px',
    textAlign: 'left',
  };

  const finalConfig = { ...defaultConfig, ...colorConfig };

  return {
    id,
    label,
    sortable: true,
    align: finalConfig.textAlign as 'left' | 'right' | 'center',
    render: (value, row) => (
      <Box
        className="new-customized-table-colored-column"
        sx={{
          color: finalConfig.color,
          backgroundColor: finalConfig.backgroundColor,
          border: `${finalConfig.borderWidth} solid ${finalConfig.borderColor}`,
          borderRadius: finalConfig.borderRadius,
          fontWeight: finalConfig.fontWeight,
          fontSize: finalConfig.fontSize,
          padding: finalConfig.padding,
          textAlign: finalConfig.textAlign,
        }}
      >
        {String(value ?? '')}
      </Box>
    ),
    ...columnOptions,
  };
};

export const CustomizedTable = <T = Record<string, unknown>,>({
  title,
  className,
  columns,
  data,
  enableSorting = false,
  enablePagination = false,
  rowsPerPageOptions = [5, 10, 25, 50, 100],
  defaultRowsPerPage = 5,
  maxHeight,
  loading = false,
  message,
  rowTestIdPrefix,
  // Server-side sorting props
  onSortChange,
  onSortReset,
  currentSortColumn,
  currentSortOrder,
  // Server-side pagination props
  onPageChange,
  onPageSizeChange,
  currentPage,
  totalItems,
  // Expandable rows props
  expandableRows = false,
  expandedContent,
  showExpandIcon,
  getRowId,
}: CustomizedTableProps<T>) => {
  // const theme = useTheme();
  // const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [order, setOrder] = useState<Order>(currentSortOrder || 'asc');
  const [orderBy, setOrderBy] = useState<keyof T | ''>(currentSortColumn || '');
  const [page, setPage] = useState(currentPage || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set()
  );

  // Sync internal state with props when they change
  useEffect(() => {
    if (currentSortColumn !== undefined) {
      setOrderBy(currentSortColumn);
    }
  }, [currentSortColumn]);

  useEffect(() => {
    if (currentSortOrder !== undefined) {
      setOrder(currentSortOrder);
    }
  }, [currentSortOrder]);

  useEffect(() => {
    if (currentPage !== undefined) {
      setPage(currentPage);
    }
  }, [currentPage]);

  // Handle row expansion toggle
  const handleRowToggle = (rowId: string | number) => {
    if (!expandableRows) return;

    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  // Default row ID getter
  const defaultGetRowId = (row: T, index: number): string | number => {
    if (getRowId) return getRowId(row);
    if (typeof row === 'object' && row !== null) {
      const obj = row as Record<string, unknown>;
      if ('id' in obj && obj['id'] !== undefined)
        return obj['id'] as string | number;
      if ('uuid' in obj && obj['uuid'] !== undefined)
        return obj['uuid'] as string | number;
    }
    return index;
  };

  // Calculate responsive height
  // const responsiveHeight =
  //   smallScreenHeight && largeScreenHeight
  //     ? isSmallScreen
  //       ? smallScreenHeight
  //       : largeScreenHeight
  //     : height;

  const handleRequestSort = (property: keyof T) => {
    if (!enableSorting) return;

    // Cycle: no sort → asc → desc → no sort
    if (orderBy !== property) {
      setOrder('asc');
      setOrderBy(property);
      if (onSortChange) onSortChange(property, 'asc');
    } else if (order === 'asc') {
      setOrder('desc');
      setOrderBy(property);
      if (onSortChange) onSortChange(property, 'desc');
    } else {
      setOrderBy('');
      if (onSortReset) onSortReset();
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    // If server-side pagination callback is provided, use it
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    setPage(0);

    // If server-side pagination callbacks are provided, use them
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
    if (onPageChange) {
      onPageChange(0);
    }
  };

  // Use server-side data directly when server-side callbacks are provided

  const hasServerSorting = Boolean(onSortChange);
  const hasServerPagination = Boolean(onPageChange);
  const sortedData =
    hasServerSorting || !enableSorting || !orderBy
      ? data // Use data as-is for server-side sorting or when sorting is disabled
      : [...data].sort((a, b) => {
          // Client-side sorting
          const key = String(orderBy);
          const aValue = (a as Record<string, unknown>)[key];
          const bValue = (b as Record<string, unknown>)[key];

          // Handle null/undefined values
          if (aValue == null && bValue == null) return 0;
          if (aValue == null) return order === 'asc' ? -1 : 1;
          if (bValue == null) return order === 'asc' ? 1 : -1;

          // Convert to string for comparison if not already comparable types
          const aCompare =
            typeof aValue === 'string' || typeof aValue === 'number'
              ? aValue
              : String(aValue);
          const bCompare =
            typeof bValue === 'string' || typeof bValue === 'number'
              ? bValue
              : String(bValue);

          if (aCompare < bCompare) {
            return order === 'asc' ? -1 : 1;
          }
          if (aCompare > bCompare) {
            return order === 'asc' ? 1 : -1;
          }
          return 0;
        });

  const paginatedData = hasServerPagination
    ? sortedData // Use data as-is for server-side pagination (server handles pagination)
    : enablePagination
    ? sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : sortedData; // Use all data when pagination is disabled

  return (
    <Box>
      {title && (
        <Typography
          variant="h6"
          component="div"
          className="new-customized-table-title"
        >
          {title}
        </Typography>
      )}
      <Box>
        <TableContainer
          component={Paper}
          className={`new-customized-table-container${
            className ? ` ${className}` : ''
          }`}
          sx={{
            maxHeight: maxHeight,
          }}
        >
          <Table className="new-customized-table" aria-label="customized table">
            <TableHead className="new-customized-table-head">
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="new-customized-table-header-cell"
                >
                  <Box className="new-customized-table-header-row-container">
                    {expandableRows &&
                      data.some((row) =>
                        showExpandIcon ? showExpandIcon(row) : true
                      ) && <Box sx={{ width: '28px', flexShrink: 0 }} />}
                    {columns.map((column) => (
                      <Box
                        key={String(column.id)}
                        className="new-customized-table-header-column"
                        sx={{
                          ...(column.flex
                            ? { flex: column.flex }
                            : column.width
                            ? { width: column.width, flexShrink: 0 }
                            : column.flex
                            ? { flex: column.flex }
                            : { flex: 1 }),
                          textAlign: column.align || 'left',
                        }}
                      >
                        {enableSorting && column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id && orderBy !== ''}
                            direction={
                              orderBy === column.id && orderBy !== ''
                                ? order
                                : 'asc'
                            }
                            onClick={() => handleRequestSort(column.id)}
                            className="new-customized-table-sort-label"
                            data-testid={
                              column.headerTestId
                                ? `sort-${column.headerTestId}`
                                : undefined
                            }
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          <Box
                            component="span"
                            data-testid={column.headerTestId}
                          >
                            {column.label}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="new-customized-table-loading-cell"
                  >
                    <CircularProgress
                      size={40}
                      className="new-customized-table-loading-spinner"
                    />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="new-customized-table-no-results-cell"
                    sx={{
                      height: '30vh',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      padding: '40px 20px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#000',
                        fontSize: '24px',
                      }}
                    >
                      <Box>
                        {message ??
                          'There are no results for the filters you selected.'}
                      </Box>
                      {!message && <Box>Please modify your search.</Box>}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const rowId = defaultGetRowId(row, index);
                  const rowIndex = index + 1;
                  const isExpanded = expandedRows.has(rowId);
                  const shouldShowExpandIcon = showExpandIcon
                    ? showExpandIcon(row)
                    : true;

                  return (
                    <TableRow
                      key={rowId}
                      data-testid={
                        rowTestIdPrefix
                          ? `${rowTestIdPrefix}-row_${rowIndex}`
                          : undefined
                      }
                    >
                      <TableCell
                        colSpan={columns.length}
                        className="new-customized-table-data-row-cell"
                        sx={{
                          padding: index === 0 ? '10px 0 2.5px 0' : '2.5px 0',
                        }}
                      >
                        <Box
                          className="new-customized-table-data-row-container"
                          sx={{
                            flexDirection: 'column',
                          }}
                        >
                          {/* Main row content */}
                          <Box sx={{ display: 'flex', width: '100%' }}>
                            {columns.map((column, colIndex) => (
                              <Box
                                key={String(column.id)}
                                className="new-customized-table-data-column"
                                sx={{
                                  ...(column.width
                                    ? { width: column.width, flexShrink: 0 }
                                    : column.flex
                                    ? { flex: column.flex }
                                    : { flex: 1 }),
                                  textAlign: column.align || 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                {expandableRows &&
                                  colIndex === 0 &&
                                  shouldShowExpandIcon && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRowToggle(rowId)}
                                      aria-label={
                                        isExpanded
                                          ? 'collapse row'
                                          : 'expand row'
                                      }
                                      sx={{
                                        marginRight: '0px',
                                        paddingtop: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      {isExpanded ? (
                                        <KeyboardArrowDownIcon
                                          sx={{ fontSize: '20px' }}
                                        />
                                      ) : (
                                        <KeyboardArrowRightIcon
                                          sx={{ fontSize: '20px' }}
                                        />
                                      )}
                                    </IconButton>
                                  )}
                                <Box
                                  sx={{
                                    flex:
                                      expandableRows && colIndex === 0
                                        ? 1
                                        : 'unset',
                                  }}
                                >
                                  {column.render
                                    ? column.render(
                                        (row as Record<string, unknown>)[
                                          String(column.id)
                                        ] as T[keyof T],
                                        row,
                                        rowIndex
                                      )
                                    : String(
                                        (row as Record<string, unknown>)[
                                          String(column.id)
                                        ] ?? ''
                                      )}
                                </Box>
                              </Box>
                            ))}
                          </Box>

                          {/* Expanded content as second line within the same row box */}
                          {expandableRows && isExpanded && expandedContent && (
                            <Collapse
                              in={isExpanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  marginTop: 1,
                                  paddingBottom: '8px',
                                  boxSizing: 'border-box',
                                }}
                              >
                                {expandedContent(rowId, row)}
                              </Box>
                            </Collapse>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {enablePagination && !loading && (
          <Box className="new-customized-table-pagination-container">
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={hasServerPagination ? totalItems || 0 : data.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page"
              ActionsComponent={(props) => (
                <Box
                  className="new-customized-table-pagination-actions-container"
                  style={{ marginLeft: '40px' }}
                >
                  <IconButton
                    onClick={(e) => handleChangePage(e, page - 1)}
                    disabled={page === 0}
                    className="new-customized-table-pagination-icon-button"
                    data-testid="previous-page-button"
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    onClick={(e) => handleChangePage(e, page + 1)}
                    disabled={
                      page >=
                      Math.ceil(
                        (hasServerPagination ? totalItems || 0 : data.length) /
                          rowsPerPage
                      ) -
                        1
                    }
                    className="new-customized-table-pagination-icon-button"
                    data-testid="next-page-button"
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              className="new-customized-table-pagination"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
